import {
  QuantityType,
  QUANTITY_TYPE_OPTIONS
} from '@components/UI-Components/QuantityType/constants/QuantityType'
import { IQuantityType } from '@components/UI-Components/QuantityType/models/QuantityType'
import { config } from '@config'
import { OrderType } from '@modules/common/models/enums'
import ClientTypeChannelMap from '@modules/common/models/enums/ClientTypeChannelMap'
import { VIPChannel } from '@modules/common/models/enums/VIPChannel'
import { VIPClientType } from '@modules/common/models/enums/VIPClientType'
import { nav, stores } from '@stores'
import { getEnumValue } from '@utils/get-enum-value'
import includes from 'lodash/includes'
import isEqual from 'lodash/isEqual'
import map from 'lodash/map'
import filter from 'lodash/filter'
import pullAll from 'lodash/pullAll'
import { action, computed, observable, reaction } from 'mobx'
import { computedFn } from 'mobx-utils'
import { View } from '../../constants/views'
import { OrderDataStore } from './OrderDataStore'
import buyingSessionGroupStore from '@stores/BuyingSessionGroup'
import { hasUserRoles, ORDER_PERMISSION_MAP } from '@services/userRoleService'
import { DefaultConfig } from '@constants'
import { IBuyingGroupDeliveryWindow } from '@modules/showroom/basketAndOrders-new/components/CellTemplates/DeliveryDate/DeliveryDatePicker/DeliveryDatePicker'

class QuantityTypeStore implements IQuantityType {
  @observable type: string
  @observable options: string[]
  @action
  setType = (quantityType: string) => {
    this.type = quantityType
  }
  /**
   * set qty options based on selected channel
   */
  @action
  setOptions = (options: string[]) => {
    this.options = options
  }

  @action
  clearAll = () => {
    this.options = null
    this.type = null
  }
}

export class OrderUIStore {
  @observable.ref view: View = (nav.queryParams.view as View) || View.Product
  // To be deprecated in favour of QuantityType
  @observable isSizedQuantity: boolean = true
  @observable isViewSelectorPopupOpen: boolean = false
  @observable isOrderDetailsModalOpen: boolean = false
  //store all other info required to go back to same view
  @observable previousSelectedData = {}
  dataStore = new OrderDataStore()
  quantityTypeStore = new QuantityTypeStore()
  private reactions = []

  @action
  setView = (view: View, subSection?: any) => {
    this.view = view
    nav.updateQueryParams({ view, subSection: subSection, viewMode: undefined })
  }

  @action
  setPreviousSelectedData = ({ appliedFilters, searchKey }) => {
    let {
      nav: {
        queryParams: { subSection }
      }
    } = stores
    this.previousSelectedData = {
      view: this.view,
      subSection,
      appliedFilters: appliedFilters,
      searchKey: searchKey
    }
  }

  @action setViewSelectorPopupState = val => {
    this.isViewSelectorPopupOpen = val
  }

  @action setOrderDetailsModalState = val => {
    if (val) {
      this.dataStore.getSummaryData().then(res => {
        this.isOrderDetailsModalOpen = val
      })
    } else {
      this.isOrderDetailsModalOpen = val
    }
  }

  // To be deprecated in favour of QuantityType
  @action
  toggleSizedQuantitySwitch = () => {
    this.isSizedQuantity = !this.isSizedQuantity
  }

  @action handleOrderNavigation = (
    orderId,
    orderType,
    clientOrderId?,
    buyingSessionId?
  ) => {
    let {
      nav: { updateQueryParams }
    } = stores
    updateQueryParams(
      {
        orderId: orderId,
        orderType: orderType,
        clientOrderId: clientOrderId,
        buyingSession: buyingSessionId ?? '',
        viewMode: undefined
      },
      'push'
    )
    this.dataStore.isOrderNavigationOpen = false
  }

  isClientOrderDisabled = computedFn(order => {
    const restictedStatus =
      config.appConfig?.enumerations?.orderStatus?.VALUES_FOR_CONFIRMED_ORDER
    return (
      order?.doorOrders?.some(ele => restictedStatus.includes(ele.status)) ||
      !filter(order?.doorOrders, doorOrder => !doorOrder?.deleted).length
    )
  })

  @computed
  get getOrderName() {
    let {
      nav: {
        queryParams: { orderType }
      }
    } = stores
    const {
      dataStore: { summary }
    } = this
    return orderType === OrderType.Door
      ? summary?.clientOrder && summary?.door
        ? `${summary.clientOrder?.name} / ${summary.door?.name}`
        : 'NA'
      : summary?.name || 'NA'
  }

  @action
  showOrdernavigation = (show: boolean) => {
    this.dataStore.isOrderNavigationOpen = show
  }

  @computed get hasDeliveryWindowPassed() {
    let hasDelivWindowPassed = false
    if (this.buyingGroupDeliveryWindow && this.buyingGroupDeliveryWindow.End) {
      const deliveryWindowCancelDate = new Date(
        this.buyingGroupDeliveryWindow.End
      ).setHours(0, 0, 0, 0)
      const currentDate = new Date().setHours(0, 0, 0, 0)
      hasDelivWindowPassed =
        new Date(currentDate).getTime() > new Date(deliveryWindowCancelDate).getTime()
    }
    return hasDelivWindowPassed
  }

  @computed get buyingGroupDeliveryWindow() {
    const deliveryWindow: IBuyingGroupDeliveryWindow = {
      Start: this.dataStore.summary?.buyingSessionGroup?.defaultDeliveryWindow?.startDate,
      End: includes(
        config?.deliveryWindowExcludedZones?.split(',') || [],
        this.dataStore.summary?.clientOrder?.client?.zoneCode ||
          this.dataStore.summary?.client?.zoneCode
      )
        ? undefined
        : this.dataStore.summary?.buyingSessionGroup?.defaultDeliveryWindow?.endDate
    }
    return deliveryWindow
  }

  @computed get isOrderEditable() {
    const allowedStatus =
      config.appConfig?.enumerations?.orderStatus?.ALLOWED_EDIT_FOR_VALUES
    const isClientMaster = this.dataStore.summary?.client?.isClientMaster
    const { orderType, clientType } = nav.queryParams

    const allowedChannel =
      clientType === getEnumValue(VIPClientType.Internal)
        ? VIPChannel.Retail
        : VIPChannel.Wholesale
    const allowedRoles = ORDER_PERMISSION_MAP.find(
      ele => ele.id === allowedChannel
    )?.allowedRoles

    if (!this.validBSGStatuses) return false

    if (isClientMaster) {
      return (
        allowedStatus.includes(this.dataStore.summary?.status) &&
        !this.dataStore.summary?.deleted &&
        hasUserRoles(allowedRoles)
      )
    } else if (orderType === OrderType.Client) {
      return (
        !this.isClientOrderDisabled(this.dataStore.summary) && hasUserRoles(allowedRoles)
      )
    }
    return (
      allowedStatus.includes(this.dataStore.summary?.status) &&
      !this.dataStore.summary?.deleted &&
      hasUserRoles(allowedRoles)
    )
  }

  @computed get validBSGStatuses() {
    const { buyingSessionGroupId, clientType } = nav.queryParams
    return buyingSessionGroupStore.hasStatusesByClientType(
      buyingSessionGroupId,
      VIPClientType[clientType] || VIPClientType.External,
      {
        [VIPChannel.Wholesale]: [
          config.appConfig.enumerations.buyingSessionStatus.BUYING_SESSION_STATUS_VALUES
            .BUYING
        ],
        [VIPChannel.Retail]: [
          config.appConfig.enumerations.retailBuyingSessionStatus
            .BUYING_SESSION_STATUS_VALUES.BUYING
        ]
      }
    )
  }

  @action
  init = () => {
    this.dataStore.initialize()
    this.setQuantityTypes(
      getEnumValue(
        ClientTypeChannelMap[
          nav.queryParams.clientType
            ? VIPClientType[nav.queryParams.clientType]
            : VIPClientType.External
        ]
      ),
      nav.queryParams.orderType === OrderType.Client
        ? this.dataStore.summary?.client?.captureSize
        : this.dataStore.summary?.clientOrder?.client?.captureSize
    )
    this.reactions.push(
      reaction(
        () => ({
          clientType: nav.queryParams.clientType,
          captureSize:
            nav.queryParams.orderType === OrderType.Client
              ? this.dataStore.summary?.client?.captureSize
              : this.dataStore.summary?.clientOrder?.client?.captureSize
        }),
        ({ clientType, captureSize }) => {
          this.setQuantityTypes(
            getEnumValue(
              ClientTypeChannelMap[
                clientType ? VIPClientType[clientType] : VIPClientType.External
              ]
            ),
            captureSize
          )
        },
        {
          equals: isEqual
        }
      )
    )
  }

  @action
  setQuantityTypes = (channel: string, captureSize: boolean) => {
    const options = map(
      QUANTITY_TYPE_OPTIONS.filter(option => {
        return (
          !option?.allowedForChannels?.length ||
          includes(option.allowedForChannels, VIPChannel[channel])
        )
      }),
      'id'
    )
    if (!captureSize) {
      pullAll(options, [QuantityType.Size])
    }
    this.quantityTypeStore.setOptions(options)
    this.quantityTypeStore.setType(
      (config.defaultQuantityType[channel] || DefaultConfig.QUANTITY_TYPE[channel]) &&
        options.includes(
          config.defaultQuantityType[channel] || DefaultConfig.QUANTITY_TYPE[channel]
        )
        ? config.defaultQuantityType[channel] || DefaultConfig.QUANTITY_TYPE[channel]
        : options[0]
    )
  }

  @action
  destroy = () => {
    this.quantityTypeStore.clearAll()
    this.reactions.forEach(dispose => dispose())
    this.reactions = []
    this.dataStore.destroy()
  }
}
